
import { useMemo, useState } from "react";
import Link from "@/components/ui/link-compat";
import {
  CheckSquare, FolderKanban, Handshake, MessageSquare,
  Plus, Pencil, Trash2, Users, Loader2, Activity, Sparkles,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NoteAttachmentGallery } from "@/components/ui/note-attachment-gallery";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { NoteEditor } from "@/components/notes/note-editor";
import { cn } from "@/lib/utils";
import type { TimelineEntry } from "@/types/crm";

// ─── Config ──────────────────────────────────────────────────────────────────

const ENTITY_CONFIG = {
  contact:  { icon: Users,       color: "text-blue-600 dark:text-blue-400",   bg: "bg-blue-50 dark:bg-blue-900/20",   label: "Contact",   route: "contact"  },
  deal:     { icon: Handshake,   color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-900/20", label: "Deal", route: "deal" },
  project:  { icon: FolderKanban,color: "text-indigo-600 dark:text-indigo-400",bg: "bg-indigo-50 dark:bg-indigo-900/20",  label: "Project",  route: "project"  },
  task:     { icon: CheckSquare, color: "text-amber-600 dark:text-amber-400",  bg: "bg-amber-50 dark:bg-amber-900/20",   label: "Task",     route: "task"     },
  dealtype: { icon: Handshake,   color: "text-gray-600 dark:text-gray-400",    bg: "bg-gray-50 dark:bg-gray-900/20",    label: "Deal Type",route: "deal"     },
} as const;

const ACTION_CONFIG = {
  created: { icon: Plus,         color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-900/20",  ring: "ring-emerald-200 dark:ring-emerald-800" },
  updated: { icon: Pencil,       color: "text-blue-600 dark:text-blue-400",       bg: "bg-blue-50 dark:bg-blue-900/20",        ring: "ring-blue-200 dark:ring-blue-800"       },
  deleted: { icon: Trash2,       color: "text-red-600 dark:text-red-400",         bg: "bg-red-50 dark:bg-red-900/20",          ring: "ring-red-200 dark:ring-red-800"         },
  note:    { icon: MessageSquare,color: "text-purple-600 dark:text-purple-400",   bg: "bg-purple-50 dark:bg-purple-900/20",    ring: "ring-purple-200 dark:ring-purple-800"   },
} as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dayLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  const diff = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: "long" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
}

function initials(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function groupEntries(entries: TimelineEntry[]): TimelineEntry[][] {
  const groups: TimelineEntry[][] = [];
  let current: TimelineEntry[] = [];

  for (const entry of entries) {
    const isBatchable =
      current.length > 0 &&
      entry.type === "change" &&
      current[0].type === "change" &&
      entry.entityId === current[0].entityId &&
      entry.action === "updated" &&
      current[0].action === "updated" &&
      Math.abs(new Date(entry.timestamp).getTime() - new Date(current[0].timestamp).getTime()) < 1000;

    if (isBatchable) {
      current.push(entry);
    } else {
      if (current.length > 0) groups.push(current);
      current = [entry];
    }
  }
  if (current.length > 0) groups.push(current);

  return groups.map((group) => {
    const seen = new Set<string>();
    return group.filter((entry) => {
      if (!entry.field) return true;
      if (seen.has(entry.field)) return false;
      seen.add(entry.field);
      return true;
    });
  });
}

// Compact feed splits into just two buckets: today and everything earlier.
function insertCompactSeparators(groups: TimelineEntry[][]): Array<{ type: "separator"; label: string } | { type: "group"; group: TimelineEntry[] }> {
  const result: Array<{ type: "separator"; label: string } | { type: "group"; group: TimelineEntry[] }> = [];
  let lastBucket = "";

  for (const group of groups) {
    const bucket = new Date(group[0].timestamp).toDateString() === new Date().toDateString()
      ? "Today"
      : "Earlier";
    if (bucket !== lastBucket) {
      result.push({ type: "separator", label: bucket });
      lastBucket = bucket;
    }
    result.push({ type: "group", group });
  }

  return result;
}

// Split groups by day for separators
function insertDaySeparators(groups: TimelineEntry[][]): Array<{ type: "separator"; label: string } | { type: "group"; group: TimelineEntry[] }> {
  const result: Array<{ type: "separator"; label: string } | { type: "group"; group: TimelineEntry[] }> = [];
  let lastDay = "";

  for (const group of groups) {
    const day = new Date(group[0].timestamp).toDateString();
    if (day !== lastDay) {
      result.push({ type: "separator", label: dayLabel(group[0].timestamp) });
      lastDay = day;
    }
    result.push({ type: "group", group });
  }

  return result;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ActorAvatar({ name }: { name?: string | null }) {
  return (
    <span
      className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-muted border border-border text-[10px] font-semibold text-muted-foreground shrink-0 mr-1.5 align-middle"
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

function DiffLines({ group }: { group: TimelineEntry[] }) {
  const rows = group.filter((change) => change.field && change.oldValue && change.newValue);
  if (rows.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
      {rows.map((change) => (
        <div key={change.id} className="truncate">
          <span className="capitalize text-foreground/60">{change.field}: </span>
          <span className="text-foreground/80">
            {change.oldValue} → {change.newValue}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ActivityTimelineProps {
  entries: TimelineEntry[];
  variant?: "full" | "compact";
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  showEntityLink?: boolean;
  currentEntityId?: string;
  onEditNote?: (noteId: string, content: string) => void;
  onDeleteNote?: (noteId: string) => void;
  onUploadFiles?: (noteId: string, files: File[]) => Promise<void>;
  onDeleteAttachment?: (noteId: string, attachmentId: string) => void;
  currentUserId?: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ActivityTimeline({
  entries,
  variant = "full",
  isLoading,
  hasMore,
  onLoadMore,
  isLoadingMore,
  showEntityLink = true,
  currentEntityId,
  onEditNote,
  onDeleteNote,
  onUploadFiles,
  onDeleteAttachment,
  currentUserId,
}: ActivityTimelineProps) {
  const isCompact = variant === "compact";
  const groups = useMemo(() => groupEntries(entries), [entries]);
  const visibleGroups = useMemo(
    () => (isCompact ? groups.slice(0, 6) : groups),
    [groups, isCompact],
  );
  const items = useMemo(
    () => (isCompact ? insertCompactSeparators(visibleGroups) : insertDaySeparators(visibleGroups)),
    [visibleGroups, isCompact],
  );
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const toggleBatch = (id: string) =>
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);

  const handleSaveNote = async (noteId: string) => {
    if (editContent.trim() && onEditNote) {
      onEditNote(noteId, editContent.trim());
    }
    if (pendingFiles.length > 0 && onUploadFiles) {
      setIsUploading(true);
      try {
        await onUploadFiles(noteId, pendingFiles);
      } finally {
        setIsUploading(false);
      }
    }
    setEditingNoteId(null);
    setPendingFiles([]);
  };

  if (isLoading) {
    return <ActivityTimelineSkeleton />;
  }

  if (groups.length === 0) {
    return (
      <EmptyState
        icon={Activity}
        title="No activity yet"
        description="Changes, notes, and updates will appear here."
        className="py-10"
      />
    );
  }

  return (
    <div className="relative">
      {/* Vertical connector line */}
      {!isCompact && (
        <div className="absolute left-[15px] top-0 bottom-0 w-px bg-gradient-to-b from-border via-border to-transparent pointer-events-none" />
      )}

      <div className="space-y-0">
        {items.map((item, idx) => {
          if (item.type === "separator") {
            return (
              <div key={`sep-${idx}`} className="relative flex items-center gap-3 py-3 pl-1">
                <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center">
                  <div className="h-1.5 w-1.5 rounded-full bg-border" />
                </div>
                <span
                  className={
                    isCompact
                      ? "font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
                      : "text-xs font-semibold text-muted-foreground/60 uppercase tracking-wider"
                  }
                >
                  {item.label}
                </span>
              </div>
            );
          }

          const { group } = item;
          const first = group[0];
          const actionStyle = ACTION_CONFIG[first.action as keyof typeof ACTION_CONFIG] ?? ACTION_CONFIG.updated;
          const ActionIcon = actionStyle.icon;
          const entityConfig = ENTITY_CONFIG[first.entityType as keyof typeof ENTITY_CONFIG];
          const isOwnEntity = first.entityId === currentEntityId;
          const isNote = first.action === "note";
          const isLastItem = idx === items.length - 1;
          const hasAssistant = group.some((entry) => entry.source === "assistant");
          const batchExpanded = expandedBatches.has(first.id);
          const collapsedBatch = group.length > 1 && !batchExpanded;

          return (
            <div key={first.id} className="relative flex gap-3 py-2.5 pl-1 group/entry">
              {/* Action icon */}
              <div
                className={cn(
                  "relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-2 ring-background shadow-sm",
                  actionStyle.bg,
                )}
              >
                <ActionIcon className={cn("h-3.5 w-3.5", actionStyle.color)} strokeWidth={2} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pb-1">
                {/* Header line */}
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  {first.userName ? (
                    <span className="text-sm font-semibold leading-snug inline-flex items-center gap-0.5">
                      <ActorAvatar name={first.userName} />
                      {first.userName}
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">System</span>
                  )}

                  {group.length > 1 && first.action === "updated" ? (
                    <button
                      type="button"
                      onClick={() => toggleBatch(first.id)}
                      aria-label={batchExpanded ? "Collapse updates" : "Expand updates"}
                      className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground"
                    >
                      {batchExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                      <span>
                        {group.length} updates to{" "}
                        {first.entityName ?? entityConfig?.label ?? "record"}
                      </span>
                    </button>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {first.action === "created" && "created"}
                      {first.action === "updated" && "updated"}
                      {first.action === "deleted" && "deleted"}
                      {first.action === "note" && "added a note"}
                    </span>
                  )}

                  {hasAssistant && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-600 dark:text-purple-400"
                      title={first.toolName ?? "assistant"}
                    >
                      <Sparkles className="h-3 w-3" /> via assistant
                    </span>
                  )}

                  {/* Show entity badge for changes always; for notes only when not on the note's own entity */}
                  {group.length <= 1 && entityConfig && (!isNote || !isOwnEntity) && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4 font-medium">
                      {entityConfig.label}
                    </Badge>
                  )}

                  {group.length > 1 ? null : showEntityLink && !isOwnEntity && entityConfig && first.entityName ? (
                    <Link
                      href={`/dashboard/${entityConfig.route}?id=${first.entityId}`}
                      prefetch={false}
                      className="text-sm font-medium hover:underline break-words"
                    >
                      {first.entityName}
                    </Link>
                  ) : (
                    !isNote && first.entityName && (
                      <span className="text-sm font-medium break-words">{first.entityName}</span>
                    )
                  )}

                  <time
                    dateTime={first.timestamp}
                    title={new Date(first.timestamp).toLocaleString()}
                    className="text-xs text-muted-foreground/60 shrink-0 ml-auto"
                  >
                    {timeAgo(first.timestamp)}
                  </time>
                </div>

                {/* Updated fields diff */}
                {first.action === "updated" && !collapsedBatch && (
                  <DiffLines group={group} />
                )}

                {/* Note card */}
                {isNote && first.noteContent && (
                  <div className="mt-2 group/note rounded-lg border border-border/60 bg-muted/20 overflow-hidden">
                    <div className="px-3 py-2.5 relative">
                      {editingNoteId === first.id ? (
                        <NoteEditor
                          value={editContent}
                          onChange={setEditContent}
                          pendingFiles={pendingFiles}
                          onPendingFilesChange={setPendingFiles}
                          noteId={first.id}
                          onDeleteAttachment={
                            onDeleteAttachment
                              ? (attId) => onDeleteAttachment(first.id, attId)
                              : undefined
                          }
                          onSubmit={() => handleSaveNote(first.id)}
                          onCancel={() => { setEditingNoteId(null); setPendingFiles([]); }}
                          submitLabel="Save"
                          submitIcon="save"
                          isSubmitting={isUploading}
                          placeholder="Edit note..."
                          autoFocus
                        />
                      ) : (
                        <>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground/90">
                            {first.noteContent}
                          </p>
                          {(onEditNote || onDeleteNote) && currentUserId && first.userId === currentUserId && (
                            <div className="absolute top-2 right-2 opacity-100 md:opacity-0 md:group-hover/note:opacity-100 transition-opacity flex gap-0.5">
                              {onEditNote && (
                                <Button
                                  size="sm" variant="ghost"
                                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    setEditingNoteId(first.id);
                                    setEditContent(first.noteContent || "");
                                    setPendingFiles([]);
                                  }}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                              {onDeleteNote && (
                                <Button
                                  size="sm" variant="ghost"
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                  aria-label="Delete note"
                                  onClick={() => setDeleteNoteId(first.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {editingNoteId !== first.id && (
                      <NoteAttachmentGallery
                        noteId={first.id}
                        onDelete={onDeleteAttachment ? (attId) => onDeleteAttachment(first.id, attId) : undefined}
                        className="px-3 pb-2.5 border-t border-border/40 pt-2"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Load more */}
      {!isCompact && hasMore && onLoadMore && (
        <div className="flex justify-center pt-4 pb-2">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading...
              </>
            ) : (
              "Load earlier activity"
            )}
          </button>
        </div>
      )}

      <DeleteConfirmation
        open={deleteNoteId !== null}
        onOpenChange={(open) => !open && setDeleteNoteId(null)}
        onConfirm={() => {
          if (deleteNoteId && onDeleteNote) onDeleteNote(deleteNoteId);
          setDeleteNoteId(null);
        }}
        entityType="note"
      />
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

export function ActivityTimelineSkeleton() {
  return (
    <div className="relative space-y-0">
      <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border/50 pointer-events-none" />
      {[0.75, 0.5, 0.85, 0.6, 0.7].map((w, i) => (
        <div key={i} className="relative flex gap-3 py-3 pl-1">
          <Skeleton className="h-8 w-8 rounded-full shrink-0 relative z-10" />
          <div className="flex-1 space-y-2 pt-1.5">
            <Skeleton className="h-3.5" style={{ width: `${w * 100}%` }} />
            {i % 2 === 0 && <Skeleton className="h-3 w-2/5" />}
          </div>
        </div>
      ))}
    </div>
  );
}
